import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Check, CheckCheck, MessageSquare, Scale, AlertTriangle, AlertCircle, CheckCircle, Clock, X, Trash2, HardDrive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ActionButton } from '@/components/ui/action-button';
import { toast } from 'sonner';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface NotificationListProps {
  onClose?: () => void;
}

export function NotificationList({ onClose }: NotificationListProps) {
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const navigate = useNavigate();
  const [notificationToRemove, setNotificationToRemove] = useState<any>(null);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'storage_warning':
        return <HardDrive className="h-4 w-4 text-orange-500" />;
      case 'storage_alert':
        return <HardDrive className="h-4 w-4 text-red-500" />;
      case 'storage_critical':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'chat_shared':
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case 'caso_shared':
        return <Scale className="h-4 w-4 text-purple-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Navigate based on notification type
    if (notification.type === 'caso_shared' && notification.metadata?.caso_id) {
      navigate(`/casos/${notification.metadata.caso_id}`);
      onClose?.();
    } else if (notification.type === 'chat_shared' && notification.metadata?.conversation_id) {
      navigate(`/chat-juridico/${notification.metadata.conversation_id}`);
      onClose?.();
    } else if (notification.type.startsWith('storage_')) {
      navigate('/pricing');
      onClose?.();
    }
  };

  const handleAcceptShare = async (notification: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    await markAsRead(notification.id);
    
    if (notification.type === 'caso_shared' && notification.metadata?.caso_id) {
      navigate(`/casos/${notification.metadata.caso_id}`);
      onClose?.();
    } else if (notification.type === 'chat_shared' && notification.metadata?.conversation_id) {
      navigate(`/chat-juridico/${notification.metadata.conversation_id}`);
      onClose?.();
    }
  };

  const handleRejectShare = async (notification: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (notification.type === 'caso_shared' && notification.metadata?.caso_id) {
        await supabase
          .from('caso_user_shares')
          .delete()
          .eq('caso_id', notification.metadata.caso_id)
          .eq('shared_with_user_id', user.id);
      } else if (notification.type === 'chat_shared' && notification.metadata?.conversation_id) {
        await supabase
          .from('conversation_user_shares')
          .delete()
          .eq('conversation_id', notification.metadata.conversation_id)
          .eq('shared_with_user_id', user.id);
      }
      
      await deleteNotification(notification.id);
      toast.success('Compartilhamento recusado');
    } catch (error) {
      console.error('Erro ao recusar compartilhamento:', error);
      toast.error('Erro ao recusar compartilhamento');
    }
  };

  const handleRemoveShare = async (notification: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotificationToRemove(notification);
  };

  const confirmRemoveShare = async () => {
    if (!notificationToRemove) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // 1. Deletar o compartilhamento
      if (notificationToRemove.type === 'caso_shared' && notificationToRemove.metadata?.caso_id) {
        await supabase
          .from('caso_user_shares')
          .delete()
          .eq('caso_id', notificationToRemove.metadata.caso_id)
          .eq('shared_with_user_id', user.id);
      } else if (notificationToRemove.type === 'chat_shared' && notificationToRemove.metadata?.conversation_id) {
        await supabase
          .from('conversation_user_shares')
          .delete()
          .eq('conversation_id', notificationToRemove.metadata.conversation_id)
          .eq('shared_with_user_id', user.id);
      }
      
      // 2. Deletar a notificação
      await deleteNotification(notificationToRemove.id);
      
      toast.success('Compartilhamento removido com sucesso');
      setNotificationToRemove(null);
    } catch (error) {
      console.error('Erro ao remover compartilhamento:', error);
      toast.error('Erro ao remover compartilhamento');
      setNotificationToRemove(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        Carregando notificações...
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-sm">Notificações</h3>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={markAllAsRead}
            className="text-xs h-7 px-2"
          >
            <CheckCheck className="h-3 w-3 mr-1" />
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/* Lista de notificações */}
      <ScrollArea className="h-[80vh]">
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Nenhuma notificação encontrada
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((notification, index) => (
              <div key={notification.id}>
                <div
                  className={cn(
                    "p-3 hover:bg-muted/50 transition-colors rounded-lg",
                    !notification.is_read && "bg-primary/5 border-l-2 border-l-primary",
                    notification.is_read && "cursor-pointer"
                  )}
                  onClick={notification.is_read ? () => handleNotificationClick(notification) : undefined}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={cn(
                          "text-sm font-medium",
                          !notification.is_read && "font-semibold"
                        )}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="h-2 w-2 bg-primary rounded-full flex-shrink-0" />
                        )}
                      </div>
                      
                      <p className="text-sm text-foreground mt-1 line-clamp-2 font-medium">
                        {notification.message}
                      </p>
                      
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: ptBR
                        })}
                      </p>

                      {notification.metadata?.expires_at && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expira {formatDistanceToNow(new Date(notification.metadata.expires_at), {
                            addSuffix: true,
                            locale: ptBR
                          })}
                        </p>
                      )}
                    </div>

                    {!notification.is_read && (notification.type === 'caso_shared' || notification.type === 'chat_shared') ? (
                      <div className="flex gap-2 flex-shrink-0 items-center self-center min-h-full">
                        <ActionButton
                          icon={Check}
                          label="Aceitar"
                          variant="accept"
                          onClick={(e) => handleAcceptShare(notification, e)}
                        />
                        <ActionButton
                          icon={X}
                          label="Recusar"
                          variant="reject"
                          onClick={(e) => handleRejectShare(notification, e)}
                        />
                      </div>
                    ) : notification.is_read && (notification.type === 'caso_shared' || notification.type === 'chat_shared') ? (
                      <div className="flex gap-2 flex-shrink-0 items-center self-center min-h-full">
                        <ActionButton
                          icon={Trash2}
                          label="Remover"
                          variant="reject"
                          onClick={(e) => handleRemoveShare(notification, e)}
                        />
                        <Check className="h-4 w-4 text-muted-foreground/60" />
                      </div>
                    ) : notification.is_read ? (
                      <Check className="h-3 w-3 text-muted-foreground/60 flex-shrink-0 mt-1" />
                    ) : null}
                  </div>
                </div>
                
                {index < notifications.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
      
      {/* Dialog de Confirmação */}
      <AlertDialog open={!!notificationToRemove} onOpenChange={() => setNotificationToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover compartilhamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Você perderá o acesso a este {notificationToRemove?.type === 'caso_shared' ? 'caso' : 'chat'} 
              e a notificação será excluída permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRemoveShare}
              className="bg-red-600 hover:bg-red-700"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}